<template>
  <div class="content">
    <div class="page-inner">
      <div class="page-header mb-0"  v-if="!fromMetadataPage">
        <router-link to="/distributions">
          <i class="fa fa-chevron-left mr-1"></i>
          {{ $t('my_releases') }}
        </router-link>
      </div>
      <div class="text-right" v-else>
        <button type="button" class="btn btn-sm gray" @click="revertMetadata()" v-if="fromMetadataPage" :disabled="!enableMetadata || loadingMetadata">
          <i class="fas fa-redo-alt"></i> {{ $t('revert_changes') }}
          <span v-if="loadingMetadata">
            <i class="fa fa-spinner fa-spin"></i>
          </span>
        </button>
        <button type="button" class="btn btn-sm btn-primary" @click="changeMetadata()" v-if="fromMetadataPage" :disabled="!Object.values(metadataChangeClass).filter(elem => elem).length || loadingMetadata">
          <i class="fas fa-save"></i> {{ $t('save_changes') }}
          <span v-if="loadingMetadata">
            <i class="fa fa-spinner fa-spin"></i>
          </span>
        </button>
      </div>
      <DistributionProgress :distribution="distribution" v-if="!fromMetadataPage"></DistributionProgress>
      <div class="row">
        <div class="col-md-12">
          <div class="card review-title-container" v-if="distribution._id">
            <div class="card-header" v-if="!fromMetadataPage">
              <div class="card-title position-relative">
                {{ $t('review_your_release') }}
              </div>
              <div class="card-category">{{ $t('once_youre_finiched') }}</div>
              <div class="button-header mb-1">
                <router-link :to="`/distributions/${distribution._id}/stores`" class="btn btn-default btn-border no-border">
                  <i class="fa fa-chevron-left mr-1"></i> {{ $t('back') }}
                </router-link>
                <button :disabled="sent" class="btn ml-1 btn-gradient btn-lg" type="submit" @click="$refs.submit.click()">{{ $t('submit') }} <i class="fa fa-rocket ml-1"></i></button>
              </div>
            </div>
          </div>

          <form @submit.prevent="validateDistribution()" v-if="distribution._id" id="distribution-review-form">
            <div class="card-body container-review" id="container-review-head" v-if="!fromMetadataPage">
              <div class="row container-review-head">
                <div class="col-sm-3">
                  <img id="cover" :src="distribution.cover_image || defaultImage + '?v=' + moment().unix()" width="100%" class="cover" :alt="distribution.artist.name" @error="onError" @load="onImgLoad" crossorigin/>
                </div>
                <div class="col-sm-9 pr-0 display-flex pt-3 pt-sm-0">
                  <div class="pos-bottom">
                    <small class="text-uppercase text-16 bold-500">{{ distribution.release_type }}</small>
                    <br>
                    <span id="title_song" class="title-distro text-60 bold-600 line-height-0-9 mb-3">{{ distribution.title }}</span>
                    <span class="bold-500">
                      <span class="avatar avatar-xs">
                        <img :src="distribution.artist.cover_image_small || distribution.artist.cover_image || defaultImage" class="avatar-img rounded-circle" :alt="distribution.artist.name" onerror="this.src = '/static/img/img-placeholder.jpg'">
                      </span>
                      &nbsp; {{ distribution.artist.name + ' ' + getPrimaryArtist }} &bull;
                      <span class="text-muted"> {{ moment(distribution.original_release_date).utc().format('Y') }} </span> &bull;
                      <span class="text-muted">
                        {{ distribution.songs.length }}&nbsp;
                        {{ (distribution.release_type === 'single') ? $t('song') : $t('songs') }}
                      </span>
                    </span>
                    <span class="bold-500 mt-2">
                      <span>
                        {{ $t('release_date') + ': ' }}
                      </span>
                      <span>
                        <b>{{ moment(distribution.release_date).utc().format('LL') }}</b>
                      </span>
                    </span>
                    <br>
                  </div>
                </div>
              </div>
            </div>
            <div class="review-songs" v-if="!fromMetadataPage">
              <div v-for="(track, index) in distribution.songs" v-bind:key="`track_${index}`">
                <div :class="{'collapsed': index}" class="btn container-review-collapse-head mt-3 w-100 line-height-2" data-toggle="collapse" :data-target="`#track-container-${index}`" :aria-controls="`track-container-${index}`">
                  <div class="float-left text-left track-buttons-container">
                    <span class="review-hide">{{ $t('song') }} #{{ index + 1 }}</span>
                    <span class="review-show">
                      {{ index + 1 }}. {{ track.title }}
                    </span>
                  </div>
                  <div class="float-right text-right track-buttons-container">
                    <div class="display-inline">
                      <i class="fas mt-2 fasAnimate fa-plus"></i>
                      <i class="fas mt-2 fasAnimate fa-minus"></i>
                    </div>
                  </div>
                </div>
                <div :id="`track-container-${index}`" :class="{'show': !index}" class="collapse review-content">
                  <div class="container-review mobile-gray pt-sm-4 pt-0">
                    <div class="row ml-0 mr-0 header pb-2 review-hide">
                      <div class="col-lg-1 text-uppercase text-muted">#</div>
                      <div class="col-lg-2 text-uppercase text-muted">{{ $t('title') }}</div>
                      <div class="col-lg-3 text-uppercase text-muted">{{ $t('contribuitors') }}</div>
                      <div class="col-lg-3 text-uppercase text-muted" v-if="track.featuring && track.featuring.length">{{ $t('featuring') }}</div>
                      <div class="col-lg-2 text-uppercase text-muted">{{ $t('track_version') }}</div>
                      <div class="col-lg-1 text-uppercase text-muted">ISRC</div>
                    </div>
                    <div class="row ml-0 mr-0 body mt-sm-0 mt-md-0 mt-lg-3 mt-0 background-mobile">
                      <div class="col-lg-1 review-hide">{{ index + 1 }}</div>
                      <div class="col-lg-2 review-hide">
                        {{ track.title }}
                      </div>
                      <div class="col-lg-3 scroll-cell">
                        <div class="review-show text-uppercase text-muted mt-3 mb-3">{{ $t('contribuitors') }}</div>
                        <div v-for="(contributor, idx) in track.contributors.data" v-bind:key="`contributor_${idx}`">
                          <span class="text-muted">{{ contributor.role }}</span>
                          &ndash;
                          {{ contributor.name }}
                        </div>
                      </div>
                      <div class="col-lg-3 scroll-cell last-child" v-if="track.featuring && track.featuring.length">
                        <div class="review-show text-uppercase text-muted mt-3 mb-3">{{ $t('featuring') }}</div>
                        <div v-for="(feat, idx) in track.featuring" v-bind:key="`featuring_${idx}`">
                          {{ feat.name }}
                          <span class="text-muted" v-if="feat.primaryArtist">({{ $t('primary') }})</span>
                        </div>
                      </div>
                      <div class="col-lg-2 review-hide">
                        {{ track.version === '' ? 'Original' : track.version }}
                      </div>
                      <div class="col-lg-1" v-if="track.isrc">
                        <div class="review-show text-uppercase text-muted mt-3 mb-3">ISRC</div>
                        {{ track.isrc }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="container-review mt-3" v-if="!fromMetadataPage">
              <div class="row ml-0 mr-0 header pb-2 review-hide">
                <div class="col-lg-4 text-uppercase text-muted">
                  {{ $t('stores') }}
                  <!-- <span class="text-white ml-1" v-if="distribution.pro_stores">({{ $t('premium_platforms') }})</span>
                  <span class="text-white ml-1" v-if="!distribution.pro_stores">({{ $t('basic_platforms') }})</span> -->
                </div>
                <div class="col-lg-3 text-uppercase text-muted" v-html="$t('youtube_content_id')"></div>
                <div class="col-lg-2 text-uppercase text-muted">{{ $t('preorder') }}</div>
              </div>
              <div class="row ml-0 mr-0 body mt-sm-3 mt-0">
                <div class="col-lg-4 no-padding">
                  <div class="review-show text-uppercase text-muted mt-1 mb-1">
                    {{ $t('stores') }}
                  </div>
                  <span v-if="distribution.pro_stores">{{ $t('premium_platforms') }}</span>
                  <span v-if="!distribution.pro_stores">{{ $t('basic_platforms') }}</span>
                  <!-- <span v-for="(dsp, index) in distribution.dsps" :key="`dsp_${index}`">
                    {{ stores[`dsp_${dsp.dsp}`] }}
                    {{ ((distribution.dsps.length - 1) !== index) ? '&bull;' : '' }}
                  </span> -->
                </div>
                <div class="col-lg-12 no-padding review-show" v-if="!distribution.pro_stores">
                  <div class="alert alert-warning mt-3 mb-0">
                    <i class="fas fa-coins text-warning mr-1"></i>
                    <span class="text-dark" v-html="$t('upgrade_deliver_more')"></span>
                    <div class="mt-2">
                      <router-link to="/upgrade?from=release-review" class="btn btn-sm btn-primary btn-block">
                        {{ $t('upgrade_now') }}
                      </router-link>
                    </div>
                  </div>
                </div>
                <div class="col-lg-12 no-padding review-show" v-if="distribution.pro_stores">
                  <div class="apple-warning mt-sm-5 mt-3">
                    <i class="fas fa-exclamation-circle"></i>
                    <span class="descriptcion" v-html="$t('apple_music_guidelines')"></span>
                  </div>
                </div>
                <div class="col-lg-3 no-padding">
                  <div class="review-show text-uppercase text-muted mt-3 mb-1" v-html="$t('youtube_content_id')"></div>
                  {{ (distribution.content_id) ? $t('yes') : $t('no') }}
                </div>
                <div class="col-lg-2 no-padding">
                  <div class="review-show text-uppercase text-muted mt-3 mb-1">{{ $t('preorder') }}</div>
                  {{ (distribution.pro_stores) ? $t('yes') : $t('no') }}
                </div>
                <div class="col-lg-12 no-padding review-hide" v-if="!distribution.pro_stores">
                  <div class="alert alert-warning mt-3 mb-0">
                    <i class="fas fa-coins text-warning mr-1"></i>
                    <span class="text-dark" v-html="$t('upgrade_deliver_more')"></span>
                    <router-link to="/upgrade?from=release-review" class="btn btn-sm btn-primary ml-1">
                      {{ $t('upgrade_now') }}
                    </router-link>
                  </div>
                </div>
                <div class="col-lg-12 no-padding review-hide" v-if="distribution.pro_stores">
                  <div class="apple-warning mt-sm-5 mt-3">
                    <i class="fas fa-exclamation-circle mr-1"></i>
                    <span class="descriptcion" v-html="$t('apple_music_guidelines')"></span>
                  </div>
                </div>
              </div>
            </div>
            <div class="container-review mt-3">
              <div class="row mr-0 ml-0">
                <div class="col-lg-6 no-padding">
                  <div class="form-group pr-0 pl-0">
                    <label for="legal_name" class="text-white">
                      {{ $t('copyright_holder') }}
                    </label>
                    <div class="input-group custom" :class="metadataChangeClass['copyright_c'] ? 'metadata-modified' : ''">
                      <div class="input-group-prepend">
                        <span class="input-group-text text-white text-14"><i class="far fa-copyright"></i></span>
                      </div>
                      <input
                        type="text" v-model="distribution.copyright_c" :class="distribution.copyright_c ? 'filled' : ''"
                        class="form-control input-border-bottom"
                        id="legal_name"
                        @change="removeEmoticons()"
                        @paste="$event.preventDefault()" @drop="$event.preventDefault()" @dragenter.prevent @dragover.prevent
                        v-on:keypress="removeEmoticonsBykey($event)"
                      >
                    </div>
                  </div>
                  <div class="form-group pr-0 pl-0" v-if="saveCopyrightHolder">
                    <label for="" class="text-white">
                      {{ $t('country') }}
                    </label>
                    <div class="select2-input">
                      <Select2 v-model="distribution.artist.legal_country" :options="countries"></Select2>
                    </div>
                  </div>
                  <div class="form-group pr-0 pl-0 pb-0">
                    <label for="label_name" class="text-white">
                      {{ $t('label_name') }} <span class="badge badge-primary">PRO</span>
                    </label>
                    <div class="input-group custom" :class="metadataChangeClass['copyright_holder'] ? 'metadata-modified' : ''">
                      <div class="input-group-prepend">
                        <span class="input-group-text text-white text-20">℗</span>
                      </div>
                      <input
                        type="text" v-model="distribution.copyright_holder" placeholder="Indiefy"
                        class="form-control pl-0"
                        id="label_name"
                        :disabled="!this.isPro"
                        @change="removeEmoticons()"
                        @paste="$event.preventDefault()" @drop="$event.preventDefault()" @dragenter.prevent @dragover.prevent
                        v-on:keypress="removeEmoticonsBykey($event)"
                      >
                      <div class="input-group-append">
                        <button class="btn btn-primary" type="button" @click="setLabelName()" v-if="!this.isPro">{{ $t('change') }}</button>
                      </div>
                    </div>
                  </div>
                  <span class="text-10" v-if="!user.subscription">{{ $t('review_go_pro') }}</span>
                </div>
                <div class="col-lg-6 row copyright-holder no-padding">
                  <div class="col-lg-4 pl-0 pr-0">&nbsp;</div>
                  <div class="col-lg-8 pl-0 pr-0 text-left">
                    <span>{{ $t('how_appear_spotify') }}</span>
                    <div class="border border-gray pt-4 pb-4 pr-4 pl-4 rounded mt-2">
                      <div class="text-18 bold-600">
                        <i class="far fa-copyright"></i> {{ distribution.copyright_c }}
                      </div>
                      <div class="text-18 bold-600">
                        <span class="text-24 bold-800">℗</span>
                        <span v-if="distribution.copyright_holder">{{ distribution.copyright_holder }}</span>
                        <span v-else>Indiefy</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="container-review mt-3" v-if="activeService._id">
              <div class="row mr-0 ml-0">
                <div class="col-lg-12 ">
                  <fieldset>
                    <legend class="text-14">
                      <span>{{ $t('selected_service_details') }}</span>
                      <!-- <button class="btn btn-danger btn-sm float-right" @click="deleteService(activeService._id)" :disabled="sent">
                        <i class="fa fa-trash" v-if="!sent"></i>
                        <i class="fa fa-spinner fa-spin" v-if="sent"></i>
                      </button> -->
                    </legend>
                    <div class="row mb-4">
                      <div class="col-md-12">
                        <div class="separator-solid"></div>
                      </div>
                      <!-- <div class="col-md-3">
                        <div class="form-group">
                          <label class="text-white">Total</label>
                          <div>{{ currency.format(activeService.total) }}</div>
                        </div>
                      </div> -->
                      <div class="col-md-6">
                        <div class="form-group">
                          <label class="text-white">{{ $t('name') }}</label>
                          <div>{{ activeService.service_type.name }}</div>
                        </div>
                      </div>
                      <!-- <div class="col-md-3">
                        <div class="form-group">
                          <label class="text-white">Payment Method</label>
                          <div class="payment-method">{{ activeService.payment_method }}</div>
                        </div>
                      </div> -->
                      <div class="col-md-6">
                        <div class="form-group">
                          <label class="text-white">{{ $t('payment_date') }}</label>
                          <div>{{ moment(activeService.paidAt).format('LLL') }}</div>
                        </div>
                      </div>
                      <div class="col-md-12">
                        <div class="separator-solid"></div>
                      </div>
                    </div>
                  </fieldset>
                </div>
              </div>
            </div>
            <div class="card-action mb-3" v-if="!fromMetadataPage">
              <div class="row">
                <div class="col-md-12 text-right mt-4">
                  <router-link
                    tag="button"
                    :to="`/distributions/${distribution._id}/stores`"
                    class="btn btn-border no-border"
                  >
                    <i class="fa fa-chevron-left mr-1"></i>
                    {{ $t('back') }}
                  </router-link>
                  <button :disabled="sent" class="btn ml-1 btn-gradient btn-lg" type="submit" ref="submit">{{ $t('submit') }}  <i class="fa fa-rocket ml-1"></i></button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>

      <!-- <div v-if="!user.subscription" class="text-center mb-3 mt-3">
        <ins class="adsbygoogle"
            style="display:block"
            data-ad-client="ca-pub-8326584585992829"
            data-ad-slot="3934111543"
            data-ad-format="auto"
            data-full-width-responsive="true"></ins>
      </div> -->
    </div>

    <div class="p-4 m-3"></div>

    <div ref="limitedDistrosModal" class="modal animated fadeInDown dark-modal w-auto" tabindex="-1" role="dialog" data-backdrop="static" data-keyboard="false">
      <div class="modal-dialog modal-center">
        <div class="modal-content">
          <div class="modal-body">
            <div class="mt-4 mb-4" style="font-size:16px;font-weight:200;">Indiefy <b>PRO</b></div>
            <div style="max-width: 160px;height: 160px;margin: 0px auto;background: url('/static/img/mesh-gradient_3.png');position: relative;background-size: cover;font-size:12px;">
              <img src="/static/img/discs.png" alt="" style="margin: auto; position: absolute; top: 0; bottom: 0; left: 0; right: 0; width: 55%;"/>
            </div>
            <div class="mt-4">
              <b>{{ $t('more_releases') }}</b><br>
              <span style="font-size: 13px; font-weight: 200;" v-if="riseLimitDistro">{{ $t('more_relaseses_descriptcion') }}</span>
              <span style="font-size: 13px; font-weight: 200;" v-else>{{ $t('more_relaseses_descriptcion_2') }}</span>
            </div>
            <br>
            <div class="container-buttons mt-2">
              <router-link to="/upgrade" >
                <button class="btn btn-gradient">{{ $t('upgrade_now') }} <i class="fa fa-rocket ml-1"></i></button>
              </router-link>
              <br><br>
              <button class="btn btn-maybe" data-dismiss="modal">{{ $t('maybe_later') }}</button>
            </div>
            <br>
            <div class="text-footer mb-4" style="font-weight: 200;">
              {{ $t('suscriptions_renew_modal') }}
              <a style="font-weight: normal;" href=" https://help.indiefy.net/hc/en-us/articles/360046365893-How-can-I-cancel-my-PRO-subscription-">{{ $t('learn_more') }}</a>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div ref="customLabelModal" class="modal animated fadeInDown dark-modal w-auto" tabindex="-1" role="dialog" data-backdrop="static" data-keyboard="false">
      <div class="modal-dialog modal-center">
        <div class="modal-content">
          <div class="modal-body">
            <div class="mt-4 mb-4" style="font-size:16px;font-weight:200;">Indiefy <b>PRO</b></div>
            <div style="max-width: 160px;height: 160px;margin: 0px auto;background: url('/static/img/mesh-gradient_2.png');position: relative;background-size: cover;font-size:12px;">
              <span class="img-container">
                &copy; {{ $t('add_custom_name') }}
                <br>
                ℗ {{ $t('add_custom_name') }}
              </span>
            </div>
            <div class="mt-4">
              <b>{{ $t('add_your_custom_name') }}</b><br>
              <span style="font-size: 13px; font-weight: 200;">{{ $t('remove_indiefy_add_own_name') }}</span>
            </div>
            <br>
            <div class="container-buttons mt-2">
              <router-link to="/upgrade" >
                <button class="btn btn-gradient">{{ $t('upgrade_now') }} <i class="fa fa-rocket ml-1"></i></button>
              </router-link>
              <br><br>
              <button class="btn btn-maybe" data-dismiss="modal">{{ $t('maybe_later') }}</button>
            </div>
            <br>
            <div class="text-footer mb-4" style="font-weight: 200;">
              {{ $t('suscriptions_renew_modal') }}
              <a style="font-weight: normal;" href=" https://help.indiefy.net/hc/en-us/articles/360046365893-How-can-I-cancel-my-PRO-subscription-">{{ $t('learn_more') }}</a>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div ref="extraDistroServicesModal" class="modal fade" tabindex="-1" role="dialog" data-backdrop="static" data-keyboard="false">
      <div class="modal-dialog modal-lg" style="max-width: 800px !important;">
        <div class="modal-content">
          <div class="modal-header pt-4 pb-4">
            <h4 class="modal-title">{{ $t('on_demand_services') }}</h4>
          </div>
          <div class="modal-body">
            <div>{{ $t('services_available_extra_distro') }}</div>
            <div class="card-list">
              <table class="table mt-4 services-table">
                <thead>
                  <tr>
                    <th>{{ $t('services') }}</th>
                    <th>{{ $t('payment_method') }}</th>
                    <th>{{ $t('payment_date') }}</th>
                    <th>{{ $t('total') }}</th>
                    <th>&nbsp;</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(service, index) in extraDistroServices" v-bind:key="`service_${index}`">
                    <td>{{ service.service_type.name }}</td>
                    <td class="payment-method">{{ service.payment_method }}</td>
                    <td><span v-if="service.paidAt">{{ moment(service.paidAt).format('LL') }}</span></td>
                    <td><span v-if="service.total">{{ currency.format(service.total) }}</span></td>
                    <td>
                      <button class="btn btn-primary btn-sm" @click="selectService(service)" :disabled="sent">
                        <span>{{ $t('select') }}</span>
                        <i class="fa fa-spinner fa-spin" v-if="sent"></i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div class="modal-footer text-center pt-5">
            <button class="btn mr-2 btn-default btn-border btn-cancel" data-dismiss="modal" @click="closeExtraDistroServicesModal()">{{ $t('close') }}</button>
          </div>
        </div>
      </div>
    </div>
    <UpgradeModal></UpgradeModal>
  </div>
</template>
<script>
import _ from 'underscore'
import Distributions from '../services/distributions'
import Labels from '../services/labels'
import Artists from '../services/artists'
import DistributionPreview from './DistributionPreview'
import Select2 from './utils/Select2'
import countries from '../data/countries'
import User from '../services/user'
import moment from 'moment'
import DistributionProgress from './utils/DistributionProgress'
import Configuration from '../services/configuration'
import ServicesOnDemand from '../services/services-on-demand'
import Public from '../services/public'
import UpgradeModal from './utils/UpgradeModal'

export default {
  name: 'DistributionReview',
  props: ['fromMetadataPage'],
  data: function () {
    return {
      sent: false,
      isPro: false,
      locationSettings: {},
      user: {},
      saveCopyrightHolder: false,
      allSelected: true,
      countries: _.map(countries, (country) => { return { text: country.name, id: country.name } }),
      distribution: {
        title: '',
        _id: '',
        copyright_holder: '',
        copyright_c: '',
        artist: {
          legal_name: '',
          legal_last_name: ''
        }
      },
      strikesCount: 0,
      moment: moment,
      defaultImage: '/static/img/img-placeholder.jpg',
      stores: {
        'dsp_746109': 'Spotify',
        'dsp_2100357': 'Deezer',
        'dsp_49262307': 'YouTube Music',
        'dsp_89882': 'Beatport',
        'dsp_1330598': 'Apple Music',
        'dsp_1499657856': 'Instagram / Facebook',
        'dsp_1809226414': 'TikTok',
        'dsp_3440259': 'TIDAL',
        'dsp_7851192': 'Pandora',
        'dsp_99268': 'Amazon',
        'dsp_1514168496': 'Boomplay',
        'dsp_103731': 'Napster',
        'dsp_3441649': 'iHeartRadio',
        'dsp_464139': '24-7 Entertainment',
        'dsp_247916': '7Digital',
        'dsp_20799134': 'Anghami',
        'dsp_847103579': 'AWA',
        'dsp_9538495': 'Gracenote',
        'dsp_1963181516': 'Resso',
        'dsp_1988507361': 'Fizy',
        'dsp_1130737243': 'Ultimate China (Aggregation Only)',
        'dsp_316911752': 'JioSaavn',
        // 'dsp_1210987244': 'United Media Agency (UMA)',
        // 'dsp_1209287754': 'Yandex Music',
        'dsp_103725': 'iMusica / Claro',
        'dsp_1232212955': 'LINE Music',
        'dsp_62873543': 'Neurotic Media',
        'dsp_78395129': 'Simfy Africa',
        'dsp_1207204780': 'TIM Music',
        'dsp_1234931270': 'Xiami',
        'dsp_121452605': 'KkBox',
        'dsp_1382854531': 'NetEase Cloud Music',
        'dsp_3287973537': 'MonkingMe',
        'dsp_4266325': 'Shazam',
        'dsp_1048705': 'Content ID (YouTube)',
        'dsp_35299696': 'SoundCloud Monetization',
        'dsp_2215088010': 'FLO Music',
        'dsp_1501350807': 'Bugs',
        'dsp_2697871022': 'Genie Music',
        'dsp_1674795424': 'Naver'
      },
      serverDefaultDate: moment().utc(),
      riseLimitDistro: null,
      reopeningOptions: [],
      regExp: /([\u2700-\u27BF]|[\uE000-\uF8FF]|\uD83C[\uDC00-\uDFFF]|\uD83D[\uDC00-\uDFFF]|[\u2011-\u26FF]|\uD83E[\uDD10-\uDDFF])/g,
      distributionOriginal: {},
      metadata_request_data: {},
      metadataChangeClass: {},
      enableMetadata: false,
      loadingMetadata: false,
      extraDistroServices: [],
      activeService: {
        _id: '',
        total: 0,
        payment_method: '',
        service_type: {},
        paidAt: ''
      },
      currency: new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })
    }
  },
  computed: {
    getPrimaryArtist: function () {
      let primaryArtistText = ''
      let primaryArtists = []
      let primaryArtistArr = []

      if (this.distribution.songs && this.distribution.songs.length === 1) {
        if (this.distribution.songs[0].featuring) {
          if (typeof this.distribution.songs[0].featuring !== 'string') {
            primaryArtists = _.filter(this.distribution.songs[0].featuring, item => item.primaryArtist)

            if (primaryArtists.length > 0) {
              _.each(primaryArtists, (item) => {
                primaryArtistArr.push(item.name.trim())
              })
              primaryArtistText += '& ' + primaryArtistArr.join(' & ') + ' '
            }
          }
        }
      }

      return primaryArtistText
    },
    hasYoutubeContentID: function () {
      return !!_.filter(this.distribution.dsps, (elem) => elem.dsp === '1048705').length
    }
  },
  methods: {
    submitDistribution: function () {
      if (this.user.strike_items && this.user.strike_items.length) {
        User.getCurrentStrikes({ userId: this.user._id }).then((response) => {
          _.each(response.data, (strike) => {
            this.strikesCount += parseInt(strike.strikes)
          })

          if (this.strikesCount) {
            return this.$router.push(`/dashboard`)
          }
        })
      }

      if (!this.distribution.cover_image) {
        return this.$notify({ message: this.$t('cover_image_not_found') }, 'warning', 'fa fa-exclamation')
      }

      if (this.saveCopyrightHolder) {
        if (!this.distribution.artist.legal_country) {
          return this.$notify({ message: this.$t('country_required') }, 'warning', 'fa-exclamation')
        }

        if (!this.distribution.copyright_c) {
          return this.$notify({ message: this.$t('copyright_required') }, 'warning', 'fa-exclamation')
        }

        if (!this.distribution.artist.legal_name) {
          this.distribution.artist.legal_name = this.distribution.copyright_c
        }

        Artists.save(this.distribution.artist).then(() => { console.log('Artist updated') })
      }

      let html = document.createElement('div')
      html.innerHTML = '<i class="fas fa-exclamation-triangle text-danger"></i><br>' + this.$t('submitting_instructions_popup') + '<br><br> ' +
      `<div class="custom-control custom-checkbox">
        <input
          type="checkbox"
          class="custom-control-input"
          id="reviewed"
        />
        <label class="custom-control-label label-break-spaces" for="reviewed"><b>` +
          this.$t('ok_i_have_reviewed') +
        `</b></label>
      </div>`
      window.swal({
        title: this.$t('read_before_submitting'),
        content: html,
        closeOnClickOutside: false,
        closeOnConfirm: true,
        buttons: {
          cancel: {
            visible: true,
            className: 'btn btn-default btn-border no-border'
          },
          confirm: {
            text: this.$t('submit'),
            className: 'btn btn-success submit-distribution disabled'
          }
        }
      }).then(confirm => {
        if (confirm) {
          if (document.querySelector('#reviewed').checked) {
            this.sent = true
            this.distribution.status = 1
            this.distribution.steps.step_review = { validate: true }
            this.distribution.fromPage = 'review'
            this.distribution.fbc = this.getCookie('_fbc') ? this.getCookie('_fbc') : localStorage.getItem('fbclid') ? localStorage.getItem('fbclid') : this.distribution.fbc
            this.distribution.fbp = this.getCookie('_fbp') ? this.getCookie('_fbp') : this.distribution.fbp
            this.distribution.userAgent = window.navigator.userAgent

            if (this.distribution.reopen_in_progress && (this.distribution.reopen_evidence && this.distribution.reopen_evidence.length > 0)) {
              this.reopening_evidence_status = 1
            }

            Distributions.save(this.distribution).then(() => {
              let songsAmount = 0

              if (this.distribution.songs && this.distribution.songs.length) {
                songsAmount = this.distribution.songs.length
              }

              window.fbq && window.fbq('track', 'Lead')
              window.fbq && window.fbq('trackCustom', 'DistributionUploaded', { tracks: songsAmount })
              window.gtag && window.gtag('event', 'conversion', {'send_to': 'AW-877256789/XmGZCMq04uYBENXAp6ID'})

              window.gtag && window.gtag('event', 'Sent', {
                hitType: 'event',
                event_category: 'Distribution',
                event_label: 'Distribution Sent'
              })

              window.swal({
                title: this.$t('done_exclamation'),
                text: this.$t('sent_to_review'),
                className: 'icon-modal',
                icon: 'success',
                buttons: {
                  confirm: {
                    text: 'Ok',
                    className: 'btn btn-primary'
                  }
                }
              })

              _.each(this.distribution.songs, (song, index) => {
                setTimeout(() => {
                  window.gtag && window.gtag('event', 'conversion', {'send_to': 'AW-323371496/_Z-6CMKX0ZEDEOiDmZoB'})
                  window.fbq && window.fbq('trackCustom', 'TrackUploaded')
                  window.gtag && window.gtag('event', `Track Uploaded`, {
                    event_category: 'Distribution',
                    event_label: 'Track Uploaded'
                  })
                }, index * 10)
              })

              window.gtag && window.gtag('event', 'page_view', {
                page_title: document.title,
                page_location: 'https://indiefy.net/distribution-sent',
                page_path: '/distribution-sent'
              })

              window.gtag && window.gtag('event', 'page_view', {
                page_title: document.title,
                page_location: 'https://indiefy.net/distribution-sent',
                page_path: '/distribution-sent',
                send_to: 'UA-80876238-1'
              })

              window.gtag && window.gtag('event', 'page_view', {
                page_title: document.title,
                page_location: 'https://indiefy.net/distribution-sent',
                page_path: '/distribution-sent',
                send_to: 'AW-877256789'
              })

              setTimeout(() => {
                this.$router.push(`/distributions?distribution_created=1`)
              }, 100)
            }, error => {
              let errorId = error.response.data.name || ''

              if (errorId === 'invalid_songs_title') {
                this.$notify({ message: this.$t('select_song_title') }, 'warning', 'fa-exclamation')
              } else if (errorId === 'unverified_restricted') {
                this.$notify({ message: this.$t('unverified_region_restricted') }, 'warning', 'fa-exclamation')
              } else {
                this.$notify({ message: 'Something wrong happened, try again or contact support' }, 'warning', 'fa-exclamation')
              }
              this.sent = false
              console.error('Error when trying to send release', error.response)

              if (errorId === 'distro_already_reviewed') {
                if (this.$route.params.id) {
                  this.$router.push(`/distributions/${this.$route.params.id}/details`)
                } else {
                  this.$router.push(`/distributions`)
                }
              }
            })
          } else {
            this.$notify({ message: this.$t('You_need_to_confirm') }, 'warning', 'fa-exclamation')
            this.submitDistribution()
          }
        } else {
          window.swal.close()
        }
      })
      setTimeout(() => {
        window.$('#reviewed').focus()
      }, 100)
      window.$(document).off('change', '#reviewed')
      window.$(document).on('change', '#reviewed', function () {
        window.$('.submit-distribution').removeClass('disabled')
        if (!this.checked) {
          window.$('.submit-distribution').addClass('disabled')
        }
      })
    },
    validateData: function () {
      if (this.distribution.status > 0) {
        if (this.$route.params.id) {
          return this.$router.push(`/distributions/${this.$route.params.id}/details`)
        } else {
          return this.$router.push(`/distributions`)
        }
      } else if (this.hasReopeningReason()) {
        if (!this.distribution.reopen_in_progress) {
          return this.$router.push(`/distributions/${this.$route.params.id}/reopening-details`)
        }
      }

      let missingFile = _.find(this.distribution.songs, song => !song.file_url)

      if (!this.distribution.songs || missingFile) {
        this.$notify({ message: this.$t('invalid_audio_detected') }, 'warning', 'fa-exclamation')
        return this.$router.push(`/distributions/${this.$route.params.id}/songs`)
      }

      if (!this.distribution.cover_image) {
        return this.$notify({ message: this.$t('cover_image_not_found') }, 'warning', 'fa fa-exclamation')
      }

      setTimeout(() => {
        this.setFontTitle()
        window.addEventListener('resize', this.setFontTitle, false)
        window.addEventListener('orientationchange', this.setFontTitle, false)
      }, 100)

      Distributions.getAvailableDistributions().then(response => {
        if (response.data.available <= 0) {
          if (response.data.reason && response.data.reason === 'unverified_restricted') {
            return this.$notify({ message: this.$t('unverified_region_restricted') }, 'warning', 'fa-exclamation')
          } else {
            this.$eventBus.$emit('showUpgradeModal', '', 'distributions_limit_review')
          }
        }
      }).catch((error) => {
        console.error('Error while trying to get distribution limit (distribution review)', error)
        this.availableDistributions = 1
      })
    },
    hasReopeningReason: function () {
      return (this.distribution.status === 0 && this.reopeningOptions.includes(this.distribution.reopening_reason))
    },
    goToDistros: function () {
      return this.$router.push(`/distributions`)
    },
    validateDistribution: function () {
      let minReleaseDate = (this.user.subscription) ? moment(this.serverDefaultDate).utc().startOf('day').add(7, 'days') : moment(this.serverDefaultDate).utc().startOf('day').add(21, 'days')

      if (moment(this.distribution.release_date).utc().startOf('day').isBefore(minReleaseDate)) {
        return this.$notify({ message: this.$t('release_date_invalid') }, 'warning', 'fa-exclamation')
      }

      new Promise(resolve => {
        if (!this.distribution.service) {
          Distributions.getAvailableDistributions().then(response => {
            if (response.data.available <= 0) {
              if (response.data.reason && response.data.reason === 'unverified_restricted') {
                return this.$notify({ message: this.$t('unverified_region_restricted') }, 'warning', 'fa-exclamation')
              } else if (!this.isPro) {
                if (this.user.verified) { // basic show modal make pro
                  this.$eventBus.$emit('showUpgradeModal', '', 'distributions_limit_review')
                } else { // basic_non_verified_alert
                  return this.$notify({ message: this.$t('basic_non_verified_alert') }, 'warning', 'fa fa-exclamation')
                }
              } else if (this.isPro && !this.user.verified) { // pro_non_verified_alert
                return this.$notify({ message: this.$t('pro_non_verified_alert') }, 'warning', 'fa fa-exclamation')
              } else {
                // return this.$notify({ message: this.$t('non_verified_alert') }, 'warning', 'fa fa-exclamation')
                return this.$notify({ message: 'There was an error submitting the request, please contact support' }, 'warning', 'fa fa-exclamation')
              }
            } else {
              resolve()
            }
          }).catch((error) => {
            console.error('Error while trying to get distribution limit (distribution review)', error)
          })
        } else {
          resolve()
        }
      }).then(() => {
        this.submitDistribution()
      })
    },
    setLabelName: function () {
      if (!this.isPro) {
        return window.$(this.$refs.customLabelModal).modal('show')
      }
    },
    onImgLoad: function (event) {
      let blockSize = 5 // only visit every 5 pixels
      let canvas = document.createElement('canvas')
      let context = canvas.getContext && canvas.getContext('2d')
      let data, width, height, length
      let i = -4
      let rgb = {r: 0, g: 0, b: 0}
      let count = 0

      height = canvas.height = event.target.naturalHeight || event.target.offsetHeight || event.target.height
      width = canvas.width = event.target.naturalWidth || event.target.offsetWidth || event.target.width
      context.drawImage(event.target, 0, 0)

      try {
        data = context.getImageData(0, 0, width, height)
        length = data.data.length

        while ((i += blockSize * 4) < length) {
          ++count
          rgb.r += data.data[i]
          rgb.g += data.data[i + 1]
          rgb.b += data.data[i + 2]
        }
        // ~~ used to floor values
        rgb.r = ~~(rgb.r / count)
        rgb.g = ~~(rgb.g / count)
        rgb.b = ~~(rgb.b / count)
        let hsp = Math.sqrt(
          0.299 * (rgb.r * rgb.r) +
          0.587 * (rgb.g * rgb.g) +
          0.114 * (rgb.b * rgb.b)
        )
        let hexFinalColor = '#' + _.map(rgb, x => {
          const hex = x.toString(16)
          return hex.length === 1 ? '0' + hex : hex
        }).join('')
        if (hsp > 166) { // light
          if (hsp >= 246) {
            hexFinalColor = this.change_brightness(hexFinalColor, 0.5)
          } else {
            hexFinalColor = this.change_brightness(hexFinalColor, 0.9)
          }
        }
        document.getElementById('container-review-head').style.background = `linear-gradient(0deg, #3c3f44 0%, ${hexFinalColor} 70%)`
      } catch (e) {
        let hex = ''
        let luma = ''

        do {
          hex = '#' + Math.floor(Math.random() * 16777215).toString(16).toLocaleUpperCase()
          let c = hex.substring(1)
          let rgb = parseInt(c, 16)
          let r = (rgb >> 16) & 0xff
          let g = (rgb >> 8) & 0xff
          let b = (rgb >> 0) & 0xff
          luma = 0.2126 * r + 0.7152 * g + 0.0722 * b
        } while (luma < 40)

        document.getElementById('container-review-head').style.background = `linear-gradient(0deg, #3c3f44 0%, ${hex} 70%)`
      }
    },
    setFontTitle: function () {
      let settings = {
        'minFontSize': -1 / 0,
        'maxFontSize': 1 / 0
      }
      let compressor = 1
      let el = document.getElementById('title_song')

      if (el) {
        el.style.fontSize = Math.max(Math.min(el.clientWidth / (compressor * 10), parseFloat(settings.maxFontSize)), parseFloat(settings.minFontSize)) + 'px'
      }
    },
    change_brightness: function (hex, rate) {
      if (typeof hex === 'string') {
        hex = hex.replace(/^\s*#|\s*$/g, '')
      } else {
        hex = hex.toString(16)
      }
      if (hex.length === 3) {
        hex = hex.replace(/(.)/g, '$1$1')
      } else {
        hex = ('000000' + hex).slice(-6)
      }
      let r = parseInt(hex.substr(0, 2), 16)
      let g = parseInt(hex.substr(2, 2), 16)
      let b = parseInt(hex.substr(4, 2), 16)
      let h
      let s
      let v
      let hsv = []
      let rgb = []
      hsv = this.rgb2hsv(r, g, b)
      h = hsv[0]
      s = hsv[1]
      v = hsv[2]
      v = parseInt(v * rate)
      rgb = this.hsv2rgb(h, s, v)
      r = rgb[0]
      g = rgb[1]
      b = rgb[2]
      hex = ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)
      return '#' + hex.split('.')[0]
    },
    rgb2hsv: function (r, g, b) {
      let v = Math.max(r, g, b)
      let n = v - Math.min(r, g, b)
      let h = n && ((v === r) ? (g - b) / n : ((v === g) ? 2 + (b - r) / n : 4 + (r - g) / n))
      return [60 * (h < 0 ? h + 6 : h), v && n / v, v]
    },
    hsv2rgb: function (h, s, v) {
      let f = (n, k = (n + h / 60) % 6) => v - v * s * Math.max(Math.min(k, 4 - k, 1), 0)
      return [f(5), f(3), f(1)]
    },
    onError: function (event) {
      event.target.src = this.defaultImage
    },
    getCookie: function (name) {
      const value = `; ${document.cookie}`
      const parts = value.split(`; ${name}=`)

      if (parts.length === 2) return parts.pop().split(';').shift()
    },
    removeEmoticons: function () {
      this.distribution.copyright_c = (this.distribution.copyright_c) ? this.distribution.copyright_c.replace(this.regExp, '') : ''
      this.distribution.copyright_holder = (this.distribution.copyright_holder) ? this.distribution.copyright_holder.replace(this.regExp, '') : ''
    },
    removeEmoticonsBykey: function (e) {
      let char = String.fromCharCode(e.keyCode)

      if (!this.regExp.test(char) && char !== '-') return true
      else e.preventDefault()
    },
    changeMetadata: function () {
      if (this.fromMetadataPage) {
        window.swal({
          title: this.$t('are_you_sure'),
          buttons: {
            cancel: {
              visible: true,
              text: this.$t('cancel'),
              className: 'btn btn-default btn-border no-border'
            },
            confirm: {
              text: this.$t('ok'),
              className: 'btn btn-primary'
            }
          }
        }).then(confirm => {
          if (!confirm) {
            return
          }
          this.loadingMetadata = true
          Distributions.saveMetadaRequest(this.distribution._id, this.name, this.metadata_request_data[this.name]).then(response => {
            this.$eventBus.$emit('notifyMetadataModified', {origin: this.name, data: this.metadata_request_data[this.name]})
            this.$notify({ message: this.$t('changes_saved') }, 'success', 'fa-check')
            this.enableMetadata = !!_.find(this.metadata_request_data[this.name], elem => elem)
            this.loadingMetadata = false
            this.$forceUpdate()
          })
        })
      }
    },
    revertMetadata: function () {
      if (this.fromMetadataPage) {
        window.swal({
          title: this.$t('are_you_sure'),
          buttons: {
            cancel: {
              visible: true,
              text: this.$t('cancel'),
              className: 'btn btn-default btn-border no-border'
            },
            confirm: {
              text: this.$t('ok'),
              className: 'btn btn-primary'
            }
          }
        }).then(confirm => {
          if (!confirm) {
            return
          }
          this.loadingMetadata = true
          Distributions.revertChangesMetadaRequest(this.distribution._id, this.name).then(response => {
            this.distribution.copyright_holder = JSON.parse(JSON.stringify(this.distributionOriginal)).copyright_holder
            this.distribution.copyright_c = JSON.parse(JSON.stringify(this.distributionOriginal)).copyright_c
            this.metadata_request_data[this.name] = {}
            this.enableMetadata = !!_.find(this.metadata_request_data[this.name], elem => elem)
            this.metadataChangeClass = {}
            this.$eventBus.$emit('notifyMetadataModified', {origin: this.name, data: this.metadata_request_data[this.name]})
            this.$notify({ message: this.$t('changes_saved') }, 'success', 'fa-check')
            this.loadingMetadata = false
            this.$forceUpdate()
          })
        })
      }
    },
    selectService: function (service) {
      this.sent = true
      this.distribution.service = service._id
      this.activeService = service
      new Promise(resolve => {
        if (this.distribution._id) {
          ServicesOnDemand.setServicetoDistro(service._id, this.distribution._id).then(response => {
            ServicesOnDemand.getServicesBy('extra_distro').then(response => {
              if (response.data.length) {
                this.extraDistroServices = response.data
              }
              resolve()
            })
          })
        } else {
          resolve()
        }
      }).then(() => {
        this.sent = false
        this.$notify({ message: this.$t('service_selected') }, 'success', 'fa-check')
        window.$(this.$refs.extraDistroServicesModal).modal('hide')
      })
    },
    closeExtraDistroServicesModal: function () {
      this.activeService = {
        _id: '',
        total: 0,
        payment_method: '',
        service_type: {},
        paidAt: ''
      }
      if (!this.distribution.service) {
        this.$notify({ message: this.$t('you_have_to_select_service') }, 'warning', 'fa-exclamation')
      }
    },
    deleteService: function (id) {
      this.sent = true
      this.activeService = {
        _id: '',
        total: 0,
        payment_method: '',
        service_type: {},
        paidAt: ''
      }
      new Promise(resolve => {
        if (this.distribution._id) {
          ServicesOnDemand.deleteServiceFromDistro(id, this.distribution._id).then(response => {
            ServicesOnDemand.getServicesBy('extra_distro').then(response => {
              if (response.data.length) {
                this.extraDistroServices = response.data
              }
              resolve()
            })
          })
        } else {
          resolve()
        }
      }).then(() => {
        this.sent = false
        this.$notify({ message: this.$t('you_have_to_select_service') }, 'warning', 'fa-exclamation')
        window.$(this.$refs.extraDistroServicesModal).modal('show')
      })
    }
  },
  mounted: function () {
    Artists.getAll().then(response => {
      this.artists = _.map(response.data, item => {
        return { id: item._id, text: item.name }
      })

      if (this.$route.params.id) {
        Distributions.get(this.$route.params.id).then(distributionResponse => {
          let distributionData = distributionResponse.data

          if (!distributionData.story_theme) {
            distributionData.story_theme = '0'
          }

          if (!distributionData.copyright_c && distributionData.artist.legal_name) {
            distributionData.copyright_c = distributionData.artist.legal_name + (distributionData.artist.legal_last_name ? (' ' + distributionData.artist.legal_last_name) : '')
          } else {
            distributionData.copyright_c = ''
          }

          if (distributionData.custom_label) {
            Labels.get(distributionData.custom_label).then(response => {
              distributionData.custom_label = response.data
              _.extend(this.distribution, distributionData)

              if (!this.distribution.artist.legal_name) {
                this.saveCopyrightHolder = true
              }
              if (!this.fromMetadataPage) {
                this.validateData()
              }
            })
          } else {
            if (this.fromMetadataPage) {
              if (!distributionData.copyright_holder) {
                distributionData.copyright_holder = ''
              }
              this.distributionOriginal = JSON.parse(JSON.stringify(distributionData))
              if (!this.distributionOriginal.metadata_request_data || !this.distributionOriginal.metadata_request_data[this.name]
              ) { // contenido o basura
                this.metadata_request_data[this.name] = {}
              } else {
                this.metadata_request_data[this.name] = this.distributionOriginal.metadata_request_data[this.name]
                _.each(this.metadata_request_data[this.name], (elem, key) => {
                  distributionData[key] = elem
                  this.metadataChangeClass[key] = true
                })
              }
              this.enableMetadata = !!_.find(this.metadata_request_data[this.name], elem => elem)
            }

            this.distribution = JSON.parse(JSON.stringify(distributionData))

            if (!this.distribution.artist.legal_name) {
              this.saveCopyrightHolder = true
            }

            if (!this.fromMetadataPage) {
              this.validateData()
            }
            this.$forceUpdate()
          }
        })
      }
    })

    // if (!this.user.subscription) {
    //   setTimeout(() => {
    //     (window.adsbygoogle = window.adsbygoogle || []).push({})
    //   }, 100)
    // }

    window.scrollTo(0, 0)
  },
  created: function () {
    this.user = this.$user

    if (this.user.subscription && this.user.subscription.plan_name !== 'indiefy_lite') {
      this.isPro = true
    }

    User.getNow().then(response => {
      this.serverDefaultDate = moment.utc(response.data.now_iso)
    })

    Configuration.get({fields: ['rise_limit_distro', 'reopen_reasons_fields']}).then(response => {
      this.riseLimitDistro = response.data.rise_limit_distro
      this.reopeningOptions = _.map(response.data.reopen_reasons_fields, (elem, key) => key)
    })

    Public.getLocationSettings().then(responseSettings => {
      this.locationSettings = responseSettings.data

      if (this.locationSettings.hide_basic && !this.user.subscription) {
        return this.$router.push(`/upgrade?from=distro_limit_location`)
      }
    })
  },
  watch: {
    'distribution._id': function () {
      setTimeout(() => {
        window.$('.toggle-checkbox[data-toggle]').bootstrapToggle({
          on: this.$t('yes'),
          off: this.$t('no'),
          width: 80
        })
      }, 10)
    },
    'distribution.copyright_c': function (copyrightC) {
      if (this.fromMetadataPage) {
        let notifyData = {origin: this.name}
        if (copyrightC) {
          if (this.distributionOriginal.copyright_c !== copyrightC) {
            this.metadata_request_data[this.name].copyright_c = copyrightC
          } else {
            delete this.metadata_request_data[this.name].copyright_c
          }
          notifyData.data = this.metadata_request_data[this.name]
          this.metadataChangeClass['copyright_c'] = !!this.metadata_request_data[this.name].copyright_c
        }
        this.$eventBus.$emit('notifyMetadataChanges', notifyData)
      }
    },
    'distribution.copyright_holder': function (copyrightHolder) {
      if (this.fromMetadataPage) {
        let notifyData = {origin: this.name}
        if (copyrightHolder || copyrightHolder === '') {
          if (this.distributionOriginal.copyright_holder !== copyrightHolder) {
            this.metadata_request_data[this.name].copyright_holder = copyrightHolder
          } else {
            delete this.metadata_request_data[this.name].copyright_holder
          }
          notifyData.data = this.metadata_request_data[this.name]
          this.metadataChangeClass['copyright_holder'] = !!this.metadata_request_data[this.name].copyright_holder
        }
        this.$eventBus.$emit('notifyMetadataChanges', notifyData)
      }
    }
  },
  components: {
    DistributionPreview,
    Select2,
    DistributionProgress,
    UpgradeModal
  }
}
</script>
<style>
  .apple-warning a{
    color: #fff;
    text-decoration: underline;
  }
  .container-review .select2-search--dropdown, .container-review .select2-container--bootstrap .select2-selection,
  .container-review .select2-container--bootstrap .select2-selection--single .select2-selection__rendered,
  .container-review .select2-container--bootstrap .select2-results__option,
  .container-review .select2-container--bootstrap .select2-search--dropdown .select2-search__field,
  .container-review .select2-container--bootstrap .select2-results__option[aria-selected=true],
  .container-review .select2-container--bootstrap .select2-selection--single .select2-selection__rendered
  {
    background: #3c3f44;
    color: #fff;
  }
  .container-review .select2-container--bootstrap.select2-container--focus .select2-selection {
    border-color: #0f6dad;
    box-shadow: 0px 0px 10px 0px #0f6dad;
  }
</style>
<style lang="scss" scoped>
  .title-instructions-modal {
    font-size: 80px
  }

  .price-big {
    font-weight: 900;
    font-size: 52px;
  }
  .img-container {
    margin: 0;
    position: absolute;
    top: 50%;
    right: 0;
    width: 100%;
    transform: translateY(-50%);
    color: #24242c;
  }
  #container-review-head {
    background: linear-gradient(0deg, rgba(60,63,68,1) 10%, #3c3f44 120%);
  }
  .display-flex {
    display: flex;
  }
  .container-review-collapse-head {
    background: #3c3f44;
    color: #fff;
    padding-right: 45px;
    padding-left: 45px;
    border-radius: 10px;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }
  .container-review-collapse-head.collapsed {
    border-bottom-left-radius: 10px;
    border-bottom-right-radius: 10px;
  }
  .container-review-collapse-head + .review-content .container-review{
    border-top-left-radius: 0;
    border-top-right-radius: 0;
  }
  .container-review {
    background: #3c3f44;
    color: #fff;
    padding: 45px;
    border-radius: 10px;
    .title {
      padding: 10px 20px;
      border-radius: 5px;
    }
    .container-review-head {
      .cover {
        box-shadow: 0 3px 6px #00000029, 0 3px 6px #0000003b;
      }
    }
    .pos-bottom {
      -webkit-box-flex: 1;
      -webkit-box-orient: vertical;
      -webkit-box-direction: normal;
      display: -webkit-box;
      display: flex;
      flex: 1;
      flex-flow: column;
      justify-content: flex-end;
      z-index: 0;
      .title-distro {
        -webkit-line-clamp: 3;
        -webkit-box-orient: vertical;
        display: -webkit-box;
        overflow: hidden;
      }
    }
    .header {
      border-bottom: 1px solid #6c757d;
    }
    .body {
      .scroll-cell {
        max-height: 130px;
        overflow-y: auto;
      }
    }
    .color-warning {
      background-color: #ffab00;
    }
    .apple-warning {
      padding-top: 10px;
      background-color: #ffab00;
      padding: 20px;
      border-radius: 5px;
      font-weight: 300;
    }
    .form-group {
      .placeholder {
        color: #fff !important;
        top: 26px;
        padding: 0;
        left: 14px;
        font-weight: 400;
      }
      .form-control:focus + .placeholder, .form-control.filled + .placeholder {
        transform: translate3d(0, -50px, 0);
      }
      .input-group.floating{
        border-radius: 4px;
        transition: all ease 0.2s;
        .form-floating-label {
          width: 50%;
          padding: 0;
          &:first-child {
            input{
              padding-left: 38px;
            }
            .placeholder {
              left: 38px;
            }
          }
          &:last-child {
            margin-left: 10px;
            width: calc(50% - 10px);
          }
        }
        .input-group-text {
          background: transparent;
          border-right: none;
          i {
            color: #fff;
          }
        }
        .input-group-text {
          border: none;
        }
        input {
          padding-left: 12px;
          border: 1px solid #6c757d;
          border-radius: 4px;
          background: transparent;
          color: #fff;
          &:focus {
            border: 1px solid #0f6dad;
            box-shadow: 0px 0px 10px 0px #0f6dad !important;
          }
          &:disabled {
            background: transparent !important;
          }
        }
      }
      .input-group.custom {
        border: 1px solid #6c757d;
        border-radius: 4px;
        transition: all ease 0.2s;
        &:focus-within {
          border: 1px solid #0f6dad;
          box-shadow: 0px 0px 10px 0px #0f6dad;
        }
        .input-group-text {
          background: transparent;
          border-right: none;
          i {
            color: #fff;
          }
        }
        .input-group-text {
          border: none;
        }
        input {
          background: transparent;
          border: none;
          color: #fff;
          &:focus {
            border: none;
          }
          &:disabled {
            background: transparent !important;
          }
        }
      }
    }
  }
  .fasAnimate.fa-plus {
    transition: all 0.4s ease;
    transform: rotate(-90deg);
    opacity: 0;
  }

  .collapsed .fasAnimate.fa-plus {
    transform: rotate(0deg);
    opacity: 1;
  }

  .fasAnimate.fa-minus {
    transition: all 0.4s ease;
    transform: rotate(0deg);
    opacity: 1;
    margin-left: -15px;
  }

  .collapsed .fasAnimate.fa-minus {
    transform: rotate(90deg);
    opacity: 0;
  }

  .review-show {
    display: none;
  }

  .review-title-container {
    margin-bottom: 20px;
  }

  .btn-primary-new {
    background: #31ce36 !important;
    border-color: #31ce36 !important;
    color: #fff;
    transition: all .2s;
    &:hover {
      background: transparent !important;
      color: #31ce36;
    }
  }

  .button-header {
    position: absolute;
    top: 26px;
    right: 20px;
  }

  .metadata-modified {
    box-shadow: 0px 0px 3px 3px #5714ff !important;
  }

  @media screen and (max-width: 992px) {
    .container-review {
      padding: 15px !important;
    }
    .review-show {
      display: block;
    }
    .review-hide {
      display: none;
    }
    .scroll-cell {
      max-height: initial;
    }
    .last-child {
      padding-bottom: 16px;
    }
    .container-review {
      border-radius: 5px;
      .body {
        .scroll-cell {
          max-height: initial;
        }
      }
      .apple-warning {
        padding: 15px;
        font-size: 12px;
      }
    }
    .container-review-collapse-head {
      background: #2d2d2d;
      padding-right: 15px;
      padding-left: 15px;
      border-radius: 5px;
      border-bottom-left-radius: 0px;
      border-bottom-right-radius: 0px;
      &:hover {
        opacity: 1;
      }
    }
    .container-review-collapse-head.collapsed {
      border-bottom-left-radius: 5px;
      border-bottom-right-radius: 5px;
    }
    .copyright-holder {
      margin-left: 0;
      margin-right: 0;
    }
    .background-mobile {
      background: #4c4c4c;
    }
    .no-padding {
      padding: 0;
    }
    .mobile-gray {
      background: #4c4c4c !important;
    }
  }

  @media screen and (max-width: 900px) {
    .button-header {
      position: initial;
      top: initial;
      right: initial;
      margin-top: 15px;
      text-align: right;
    }
  }

  @media screen and (max-width: 480px) {
    .date_release {
      display: block;
    }
  }

  .story-themes {
    img {
      border-radius: 3px;
    }

    .pro-overlay {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(255, 255, 255, 0.7);

      span {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50% , -50%);
        -webkit-transform: translate(-50%, -50%);
      }
    }

    .col-theme:hover {
      cursor: pointer;

      .fa {
        opacity: 0.5;
        display: block !important;
      }
    }

    .fa {
      display: none;
      position: absolute;
      top: 50%;
      left: 50%;
      font-size: 40px;
      margin-top: -20px;
      margin-left: -20px;
    }

    .check-selected {
      display: block !important;
    }
  }

  .cursor-pointer {
    cursor: pointer;
  }
</style>



// WEBPACK FOOTER //
// src/components/DistributionReview.vue